import * as React from 'react';
import { Box, Button, Stack, ThemeProvider, Typography } from '@mui/material';
import { generateTheme } from '../themes';
import { useRef, useLayoutEffect } from 'react';
import { gsap } from 'gsap';

const buttonSxFor = (style) => {
    const base = {
        borderRadius: '999px',
        textTransform: 'none',
        fontWeight: 600,
        fontSize: { xs: '0.95rem', md: '1rem' },
        px: { xs: 2.5, md: 3.5 },
        py: { xs: 1, md: 1.25 },
        minHeight: 48
    };
    if (style === 'outlined') {
        return {
            ...base,
            color: 'primary.main',
            borderColor: 'primary.main',
            borderWidth: 1.5,
            '&:hover': { borderWidth: 1.5, borderColor: 'primary.main', backgroundColor: 'rgba(92, 51, 215, 0.08)' }
        };
    }
    if (style === 'outlined-contrast') {
        return {
            ...base,
            color: 'text.primary',
            borderColor: 'text.primary',
            borderWidth: 1.5,
            '&:hover': { borderWidth: 1.5, borderColor: 'text.primary', backgroundColor: 'rgba(255, 255, 255, 0.08)' }
        };
    }
    return {
        ...base,
        backgroundColor: 'primary.main',
        color: 'primary.contrastText',
        '&:hover': { backgroundColor: 'primary.dark' }
    };
};

const buttonVariantFor = (style) => (style === 'filled' ? 'contained' : 'outlined');

const MOBILE_TITLE_SIZES = {
    s: { xs: '1.5rem', sm: '1.875rem' },
    m: { xs: '1.875rem', sm: '2.375rem' },
    l: { xs: '2.25rem', sm: '3rem' }
};

const DESKTOP_TITLE_SIZES = {
    s: { md: '2.5rem', lg: '2.875rem' },
    m: { md: '3.25rem', lg: '3.75rem' },
    l: { md: '4rem', lg: '4.75rem' },
    xl: { md: '5rem', lg: '6rem' }
};

const titleFontSize = (mobile, desktop) => ({
    ...(MOBILE_TITLE_SIZES[mobile] || MOBILE_TITLE_SIZES.m),
    ...(DESKTOP_TITLE_SIZES[desktop] || DESKTOP_TITLE_SIZES.l)
});

const CtaButton = ({ className, text, url, style }) => (
    <Button
        className={className}
        href={url || '#'}
        variant={buttonVariantFor(style)}
        sx={buttonSxFor(style)}
        disableElevation
    >
        <span dangerouslySetInnerHTML={{ __html: text }} />
        <span aria-hidden="true" style={{ marginLeft: '0.4em' }}>→</span>
    </Button>
);

const FinalCtaSlice = ({ data }) => {
    const {
        colorTheme = 'dark',
        eyebrow,
        title,
        subtitle,
        titleSizeDesktop = 'l',
        titleSizeMobile = 'm',
        primaryCta,
        secondaryCta
    } = data;

    const theme = generateTheme(colorTheme);
    const containerRef = useRef(null);

    useLayoutEffect(() => {
        if (!containerRef.current) return;
        const targets = gsap.utils.toArray('.fc-animate', containerRef.current);
        targets.forEach((el, i) => {
            gsap.fromTo(
                el,
                { opacity: 0, y: 12 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.7,
                    delay: 0.08 * i,
                    ease: 'easeIn',
                    scrollTrigger: {
                        id: 'fcAnimate',
                        trigger: el,
                        start: 'top 85%',
                        markers: false
                    }
                }
            );
        });
    }, []);

    return (
        <ThemeProvider theme={theme}>
            <Box
                ref={containerRef}
                sx={{
                    background: (t) => t.palette.background.default,
                    px: { xs: 2, md: 12 },
                    py: { xs: 8, md: 14 },
                    textAlign: 'center'
                }}
            >
                {eyebrow && (
                    <Typography
                        className="fc-animate"
                        component="span"
                        sx={{
                            display: 'block',
                            color: 'text.secondary',
                            letterSpacing: '0.2em',
                            textTransform: 'uppercase',
                            fontSize: '0.8rem',
                            fontWeight: 500,
                            opacity: 0.7,
                            mb: { xs: 2, md: 2.5 }
                        }}
                        dangerouslySetInnerHTML={{ __html: eyebrow }}
                    />
                )}

                {title && (
                    <Typography
                        className="fc-animate"
                        component="h2"
                        lang="sv"
                        sx={{
                            color: 'text.primary',
                            fontFamily: 'Kanit, sans-serif',
                            fontWeight: 800,
                            textTransform: 'uppercase',
                            fontSize: titleFontSize(titleSizeMobile, titleSizeDesktop),
                            lineHeight: 1.05,
                            letterSpacing: '-0.01em',
                            maxWidth: 1020,
                            mx: 'auto',
                            overflowWrap: 'break-word',
                            hyphens: 'auto'
                        }}
                        dangerouslySetInnerHTML={{ __html: title }}
                    />
                )}

                {subtitle && (
                    <Typography
                        className="fc-animate"
                        component="p"
                        sx={{
                            color: 'text.secondary',
                            fontSize: { xs: '1rem', md: '1.125rem' },
                            lineHeight: 1.55,
                            maxWidth: 720,
                            mx: 'auto',
                            mt: { xs: 2.5, md: 3 }
                        }}
                        dangerouslySetInnerHTML={{ __html: subtitle }}
                    />
                )}

                {(primaryCta?.text || secondaryCta?.text) && (
                    <Stack
                        className="fc-animate"
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={{ xs: 1.5, sm: 2 }}
                        justifyContent="center"
                        alignItems="center"
                        sx={{ mt: { xs: 4, md: 5 } }}
                    >
                        {primaryCta?.text && (
                            <CtaButton
                                className="fc-primary-cta"
                                text={primaryCta.text}
                                url={primaryCta.url}
                                style={primaryCta.style}
                            />
                        )}
                        {secondaryCta?.text && (
                            <CtaButton
                                className="fc-secondary-cta"
                                text={secondaryCta.text}
                                url={secondaryCta.url}
                                style={secondaryCta.style}
                            />
                        )}
                    </Stack>
                )}
            </Box>
        </ThemeProvider>
    );
};

export default FinalCtaSlice;
